import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Alert,
  AlertDescription,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { Offer } from '../../../../../../../../types/Offer';
import PrefillOfferModalOffers from './PrefillOfferModalOffers';
import { useFormContext } from 'react-hook-form';
import { InsuranceDetailFormValues } from '../../../types/Form';
import useAddInsuranceModalContext from '../hooks/useAddInsuranceModalContext';
import { uuid4 } from '@sentry/utils';
import PrefillOfferModalInsurances from './PrefillOfferModalInsurances';

interface Props {
  offers: Offer[];
  isOpen: boolean;
  onClose: () => void;
}

const PrefillOfferModal: React.FC<Props> = ({ isOpen, onClose, offers }) => {
  const { t } = useTranslation();
  const { setInsuranceCompanies, setRiskObjects } =
    useAddInsuranceModalContext();
  const { reset } = useFormContext<InsuranceDetailFormValues>();

  const handleSelectOffer = (offer: Offer) => {
    // handle prefill
    const riskObject = Object.values(offer.risk_objects).flat()[0];

    reset({
      name: t(`anon_pack_types.${offer.insurance?.name}`, {
        defaultValue: offer?.insurance?.name || 'Imported offer',
      }),
      premium: offer.premium.total_premium,
      payment_interval: offer.premium.payment_interval,
      description: offer.description || undefined,
      insurance_company: offer.insurance?.company,
      documents: Object.entries(offer.documents).flatMap(([category, docs]) => {
        if (offer.documents?.signed?.length && category === 'unsigned')
          return [];
        return docs.map((doc) => ({
          ...doc,
          public_bucket_link: doc.link,
          type: 'INSURANCE_POLICY',
        }));
      }),
      links: [
        {
          filename: 'ipid',
          link: offer.insurance?.ipid,
        },
        {
          filename: 'terms',
          link: offer.insurance?.terms,
        },
      ],
      coverages: offer.guarantees.map((guarantee) => ({
        risk_object: riskObject?.name,
        risk_object_id: riskObject?.id || riskObject?.risk_object_id,
        id: uuid4(),
        name: t(`anon_pack_types.${guarantee.name}`, {
          defaultValue: offer?.insurance?.name,
        }),
        insurance_product_id: guarantee.insurance_product_id,
        premium: guarantee.total_premium,
        insurance_company: offer.insurance?.company,
        payment_interval: offer.premium.payment_interval,
      })),
    });

    if (riskObject) {
      setRiskObjects([
        {
          label: riskObject.name,
          value: riskObject.id || riskObject.risk_object_id,
        },
      ]);
    }
    setInsuranceCompanies([
      { label: offer.insurance?.company, value: offer.insurance?.company },
    ]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('ADVISORY-REPORTS_OFFER_MODAL_DETAIL_PREFILL-BUTTON')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={VStack} align="stretch" spacing="6">
          <VStack align="stretch" spacing="3">
            <VStack align="stretch" spacing="1">
              <Text textStyle="h6Headline">
                {t('ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCES_TITLE')}
              </Text>
              <Text textStyle="smallBody" color="gray.500">
                {t(
                  'ADVISORY-REPORTS_OFFER_MODAL_DETAIL_PREFILL-BUTTON_TITLE_CURRENT-INSURANCES_DESC',
                )}
              </Text>
            </VStack>
            <PrefillOfferModalInsurances onClose={onClose} />
          </VStack>
          <VStack align="stretch" spacing="3">
            <VStack align="stretch" spacing="1">
              <Text textStyle="h6Headline">
                {t(
                  'ADVISORY-REPORTS_OFFER_MODAL_DETAIL_PREFILL-BUTTON_TITLE_OFFERS',
                )}
                <Text textStyle="smallBody" color="gray.500">
                  {t(
                    'ADVISORY-REPORTS_OFFER_MODAL_DETAIL_PREFILL-BUTTON_TITLE_OFFERS_DESC',
                  )}
                </Text>
              </Text>
            </VStack>
            {offers.length ? (
              <PrefillOfferModalOffers
                offers={offers}
                onSelect={handleSelectOffer}
              />
            ) : (
              <Alert status="info">
                <AlertDescription>
                  {t(
                    'ADVISORY-REPORTS_OFFER_MODAL_DETAIL_PREFILL-BUTTON_EMPTY',
                  )}
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="grayedGhost" onClick={onClose}>
            {t('CLOSE')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PrefillOfferModal;
